import React, { useRef } from 'react';
import { Box, Typography, Avatar, AvatarGroup, Chip, Tooltip } from '@mui/material';
import { TagBox } from '../tags';
import { statusColors, laborStatusColors, getAvatarColor } from '../../theme/tokens';

const STATUS_COLORS = {
  todo: { bar: statusColors.todo.color, bg: statusColors.todo.bg, border: statusColors.todo.bg },
  in_progress: { bar: statusColors.in_progress.color, bg: statusColors.in_progress.bg, border: statusColors.in_progress.bg },
  in_review: { bar: statusColors.in_review.color, bg: statusColors.in_review.bg, border: statusColors.in_review.bg },
  done: { bar: statusColors.done.color, bg: statusColors.done.bg, border: statusColors.done.bg },
};

const WORK_STATUS_COLORS = {
  pending: laborStatusColors.pending.color,
  in_progress: laborStatusColors.in_progress.color,
  complete: laborStatusColors.complete.color,
};

const STATUS_LABELS = {
  todo: 'To Do', in_progress: 'In Progress', in_review: 'In Review', done: 'Done',
};

const WORK_STATUS_LABELS = {
  pending: 'Pending', in_progress: 'In Progress', complete: 'Complete',
};

export const Timeline = ({ tasks, workItems, users, viewMode, rangeStart, rangeEnd, onTaskClick }) => {
  const containerRef = useRef(null);

  const totalDays = Math.ceil((rangeEnd - rangeStart) / (1000 * 60 * 60 * 24));
  const LEFT_PANEL = 280; // px width for labels

  const getPosition = (date) => {
    const d = new Date(date);
    const dayOffset = (d - rangeStart) / (1000 * 60 * 60 * 24);
    return (dayOffset / totalDays) * 100;
  };

  // Get clamped bar left + width so bars stay within 0-100% of the gantt area
  const getBar = (start, end) => {
    const rawLeft = getPosition(start);
    const rawRight = getPosition(end || start) + (1 / totalDays) * 100; // +1 day min
    const left = Math.max(0, rawLeft);
    const right = Math.min(100, rawRight);
    const width = Math.max(0, right - left);
    return { left, width };
  };

  // Check if a task overlaps the visible range
  const overlapsRange = (task) => {
    if (!task.startDate) return false;
    const s = new Date(task.startDate);
    const e = task.endDate ? new Date(task.endDate) : s;
    return s <= rangeEnd && e >= rangeStart;
  };

  // Generate month labels
  const months = [];
  const monthStart = new Date(rangeStart);
  monthStart.setDate(1);
  while (monthStart <= rangeEnd) {
    const pos = getPosition(monthStart);
    const nextMonth = new Date(monthStart);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const endPos = Math.min(100, getPosition(nextMonth));
    if (pos < 100 && endPos > 0) {
      months.push({
        label: monthStart.toLocaleString('default', { month: 'short' }),
        year: monthStart.getFullYear(),
        left: Math.max(0, pos),
        width: endPos - Math.max(0, pos),
      });
    }
    monthStart.setMonth(monthStart.getMonth() + 1);
  }

  // Today position
  const today = new Date();
  const todayPos = getPosition(today);
  const showToday = todayPos >= 0 && todayPos <= 100;

  // Get work items for a task
  const getTaskWorkItems = (taskId) => (workItems || []).filter((wi) => {
    const wiTaskId = wi.task?._id || wi.task;
    return wiTaskId === taskId;
  });

  // Get work items for a user
  const getUserWorkItems = (userId) => (workItems || []).filter((wi) => {
    const wiUserId = wi.user?._id || wi.user;
    return wiUserId === userId;
  });

  // --- TASKS VIEW ---
  const renderTasksView = () => {
    const scheduledTasks = (tasks || [])
      .filter((t) => t.startDate && overlapsRange(t))
      .sort((a, b) => {
        const diff = new Date(a.startDate) - new Date(b.startDate);
        if (diff !== 0) return diff;
        return new Date(a.endDate || a.startDate) - new Date(b.endDate || b.startDate);
      });

    return scheduledTasks.map((task) => {
      const sc = STATUS_COLORS[task.status] || STATUS_COLORS.todo;
      const taskWIs = getTaskWorkItems(task._id);
      const completedWIs = taskWIs.filter((wi) => wi.status === 'complete').length;
      const progress = taskWIs.length > 0 ? (completedWIs / taskWIs.length) * 100 : 0;
      const { left, width } = getBar(task.startDate, task.endDate);

      return (
        <Box key={task._id} sx={{
          display: 'flex', mb: 1, minHeight: 56, cursor: 'pointer',
          borderRadius: 2, border: `1px solid ${sc.border}`, backgroundColor: sc.bg,
          '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
          overflow: 'hidden',
        }} onClick={() => onTaskClick?.(task)}>
          {/* Left label */}
          <Box sx={{ width: LEFT_PANEL, minWidth: LEFT_PANEL, p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRight: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {task.title}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {new Date(task.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                {' – '}
                {task.endDate ? new Date(task.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
              </Typography>
              {task.tags && task.tags.length > 0 && (
                <TagBox tags={task.tags} count={2} sx={{ mt: 0.25 }} />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 1 }}>
              <Chip label={STATUS_LABELS[task.status] || task.status} size="small"
                sx={{ height: 20, fontSize: 9, fontWeight: 700, backgroundColor: sc.bar, color: '#fff' }} />
              {taskWIs.length > 0 && (
                <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 20, height: 20, fontSize: 9 } }}>
                  {taskWIs.map((wi) => (
                    <Tooltip key={wi._id} title={wi.user?.name || ''} arrow>
                      <Avatar sx={{ bgcolor: getAvatarColor(wi.user?._id), border: `2px solid ${WORK_STATUS_COLORS[wi.status] || WORK_STATUS_COLORS.pending} !important` }}>
                        {wi.user?.name?.[0]?.toUpperCase() || '?'}
                      </Avatar>
                    </Tooltip>
                  ))}
                </AvatarGroup>
              )}
            </Box>
          </Box>
          {/* Right gantt area */}
          <Box sx={{ flex: 1, position: 'relative', minHeight: 40, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
            {width > 0 && (
              <Box sx={{
                position: 'absolute',
                left: `${left}%`,
                width: `${width}%`,
                height: 8, borderRadius: 4,
                backgroundColor: `${sc.bar}30`,
                overflow: 'hidden',
              }}>
                <Box sx={{ height: '100%', width: `${progress}%`, backgroundColor: sc.bar, borderRadius: 4, transition: 'width 0.3s' }} />
              </Box>
            )}
          </Box>
        </Box>
      );
    });
  };

  // --- PEOPLE VIEW ---
  const renderPeopleView = () => {
    const allUsers = users || [];
    // Sort: users with work items first
    const sorted = [...allUsers].sort((a, b) => {
      const aWIs = getUserWorkItems(a._id).length;
      const bWIs = getUserWorkItems(b._id).length;
      return bWIs - aWIs;
    });

    return sorted.map((user) => {
      const userWIs = getUserWorkItems(user._id);
      // For each work item, get the task's dates
      const bars = userWIs.map((wi) => {
        const t = (tasks || []).find((task) => task._id === (wi.task?._id || wi.task));
        if (!t || !t.startDate || !overlapsRange(t)) return null;
        return { ...wi, taskData: t };
      }).filter(Boolean).sort((a, b) => new Date(a.taskData.startDate) - new Date(b.taskData.startDate));

      // Check for overlaps (simplified: any two bars that overlap in time)
      const hasOverlap = bars.some((bar, i) =>
        bars.some((other, j) => {
          if (i >= j) return false;
          const aStart = new Date(bar.taskData.startDate);
          const aEnd = new Date(bar.taskData.endDate || bar.taskData.startDate);
          const bStart = new Date(other.taskData.startDate);
          const bEnd = new Date(other.taskData.endDate || other.taskData.startDate);
          return aStart <= bEnd && bStart <= aEnd;
        })
      );

      return (
        <Box key={user._id} sx={{
          display: 'flex', mb: 1, minHeight: 48,
          borderRadius: 2, border: `1px solid ${hasOverlap ? statusColors.in_review.bg : ''}`,
          borderColor: hasOverlap ? statusColors.in_review.color : 'divider',
          backgroundColor: hasOverlap ? statusColors.in_review.bg : 'background.paper',
          overflow: 'hidden',
        }}>
          {/* Left label */}
          <Box sx={{ width: LEFT_PANEL, minWidth: LEFT_PANEL, p: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderRight: '1px solid', borderColor: 'divider' }}>
            <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: getAvatarColor(user._id) }}>
              {user.name?.[0]?.toUpperCase() || '?'}
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
                {user.name}
              </Typography>
              {bars.length > 0 ? (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {bars.length} item{bars.length !== 1 ? 's' : ''}
                  {hasOverlap && <span style={{ color: WORK_STATUS_COLORS.pending, fontWeight: 700 }}> (overlap)</span>}
                </Typography>
              ) : (
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>Available</Typography>
              )}
            </Box>
          </Box>
          {/* Right gantt area */}
          <Box sx={{ flex: 1, position: 'relative', minHeight: 40, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
            {bars.map((bar) => {
              const { left, width } = getBar(bar.taskData.startDate, bar.taskData.endDate);
              const barColor = WORK_STATUS_COLORS[bar.status] || WORK_STATUS_COLORS.pending;
              if (width <= 0) return null;
              return (
                <Tooltip key={bar._id} title={`${bar.taskData.taskNumber}: ${bar.taskData.title} (${WORK_STATUS_LABELS[bar.status] || bar.status})${bar.time ? ` — ${bar.time}h` : ''}`} arrow>
                  <Box onClick={(e) => { e.stopPropagation(); onTaskClick?.(bar.taskData); }}
                    sx={{
                      position: 'absolute',
                      left: `${left}%`,
                      width: `${width}%`,
                      height: 20, borderRadius: 10,
                      backgroundColor: barColor, opacity: 0.85,
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      '&:hover': { opacity: 1, boxShadow: '0 2px 6px rgba(0,0,0,0.15)' },
                      transition: 'opacity 0.2s',
                      zIndex: 1,
                    }}>
                    <Typography variant="caption" sx={{ color: '#fff', fontSize: 9, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', px: 0.75 }}>
                      {bar.taskData.taskNumber}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      );
    });
  };

  return (
    <Box ref={containerRef}>
      {/* Month headers + today line container */}
      <Box sx={{ display: 'flex', mb: 1 }}>
        <Box sx={{ width: LEFT_PANEL, minWidth: LEFT_PANEL }} />
        <Box sx={{ flex: 1, position: 'relative', display: 'flex', borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}>
          {months.map((m, i) => (
            <Box key={i} sx={{ position: 'absolute', left: `${m.left}%`, width: `${m.width}%`, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 12 }}>
                {m.label}
              </Typography>
              {months.length <= 6 && (
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 9, display: 'block' }}>
                  {m.year}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Rows with today line overlay */}
      <Box sx={{ display: 'flex' }}>
        <Box sx={{ width: LEFT_PANEL, minWidth: LEFT_PANEL }} />
        <Box sx={{ flex: 1, position: 'relative' }}>
          {/* Today line */}
          {showToday && (
            <Box sx={{
              position: 'absolute', top: -24, bottom: 0,
              left: `${todayPos}%`,
              width: 0, borderLeft: '2px solid',
              borderColor: 'error.main',
              zIndex: 10, pointerEvents: 'none',
            }}>
              <Chip label="Today" size="small" sx={{
                position: 'absolute', top: 0, left: -24,
                height: 18, fontSize: 9, fontWeight: 700,
                backgroundColor: 'error.main', color: '#fff',
              }} />
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={{ position: 'relative' }}>
        {viewMode === 'tasks' ? renderTasksView() : renderPeopleView()}

        {/* Empty state */}
        {viewMode === 'tasks' && (tasks || []).filter((t) => t.startDate).length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
              No scheduled tasks. Add start/end dates to tasks to see them on the timeline.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
